import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { updatePost, fetchPostBySlug, fetchBlogById, fetchBlogCategories } from '../store/blogSlice';
import { selectCurrentPost, selectCurrentBlog, selectCurrentBlogCategories } from '../store/selectors';
import { blogAPI } from '../api/blogApi';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loading } from '@/components/ui/loading';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { ImageUploadSection } from '../components/ImageUploadSection';
import type { PostImage } from '../components/ImageUploadSection';

export const EditPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const post = useAppSelector(selectCurrentPost);
  const blog = useAppSelector(selectCurrentBlog);
  const categories = useAppSelector(selectCurrentBlogCategories);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categoryId: 'none',
  });
  const [images, setImages] = useState<PostImage[]>([]);
  const [existingImages, setExistingImages] = useState<PostImage[]>([]);
  const [coverImageId, setCoverImageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Fetch post by slug
  useEffect(() => {
    if (slug) {
      setInitialLoading(true);
      setLoadError(false);
      dispatch(fetchPostBySlug(slug))
        .unwrap()
        .catch((error) => {
          console.error('Failed to load post:', error);
          setLoadError(true);
          setInitialLoading(false);
          toast.error('Không thể tải bài viết');
        });
    }
  }, [slug, dispatch]);

  // Fetch blog when we have post
  useEffect(() => {
    if (post && post.blog_id && post.slug === slug) {
      dispatch(fetchBlogById(post.blog_id));
    }
  }, [post, slug, dispatch]);

  // Fetch categories when we have blog that matches post
  useEffect(() => {
    if (blog && post && blog.id === post.blog_id) {
      dispatch(fetchBlogCategories({ blogId: blog.id }));
    }
  }, [blog, post, dispatch]);

  // Populate form when post is loaded AND matches current slug
  useEffect(() => {
    if (post && post.slug === slug) {
      setFormData({
        title: post.title,
        content: post.content,
        categoryId: post.category?.id?.toString() || 'none',
      });
      setCoverImageId(post.cover_image_id ?? null);

      // Load existing images
      if (post.id) {
        blogAPI.public.getPostImages(post.id)
          .then((response) => {
            const postImages: PostImage[] = response.data.map((img, idx) => ({
              id: img.id,
              url: img.url,
              type: img.type,
              caption: img.caption,
              sort_order: img.sort_order ?? idx,
              driveFileId: img.drive_file_id ?? undefined,
              drive_file_id: img.drive_file_id,
              drive_owner_id: img.drive_owner_id,
            }));
            setImages(postImages);
            setExistingImages(postImages); // Track original images
          })
          .catch((error) => {
            console.error('Failed to load images:', error);
          });
      }

      setInitialLoading(false);
    }
  }, [post, slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!post) {
      toast.error('Không tìm thấy bài viết');
      return;
    }

    if (!formData.title.trim()) {
      toast.error('Vui lòng nhập tiêu đề bài viết');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Vui lòng nhập nội dung bài viết');
      return;
    }

    // Check if any images are still uploading
    const uploadingImages = images.filter(img => img.isUploading);
    if (uploadingImages.length > 0) {
      toast.error(`Vui lòng đợi ${uploadingImages.length} ảnh/video đang upload hoàn tất`);
      return;
    }

    setLoading(true);
    try {
      // Step 1: Update post content
      const result = await dispatch(
        updatePost({
          id: post.id,
          data: {
            category_id: formData.categoryId !== 'none' ? parseInt(formData.categoryId) : undefined,
            title: formData.title.trim(),
            content: formData.content.trim(),
          },
        })
      ).unwrap();

      // Step 2: Handle image changes
      // Find deleted images (existed before but not in current list)
      const deletedImages = existingImages.filter(
        (existing) => !images.find((img) => img.id === existing.id)
      );

      // Delete removed images
      for (const img of deletedImages) {
        if (img.id) {
          try {
            await blogAPI.admin.removePostImage(post.id, img.id);
          } catch (error) {
            console.error('Failed to delete image:', error);
          }
        }
      }

      // Find new images (no ID means newly added) and filter out blob URLs
      const newImages = images.filter((img) => !img.id && !img.url.startsWith('blob:'));

      if (newImages.length < images.filter(img => !img.id).length) {
        console.warn('[EditPost] Skipped blob URLs - upload not complete');
      }

      // Add new images
      if (newImages.length > 0) {
        await blogAPI.admin.addPostImagesBatch(post.id, newImages.map((image) => {
          // Calculate sort_order based on position in final array
          const finalSortOrder = images.indexOf(image);
          return {
            url: image.url,
            type: image.type,
            caption: image.caption,
            sort_order: finalSortOrder,
            drive_file_id: image.drive_file_id ?? undefined,
            drive_owner_id: image.drive_owner_id ?? undefined,
          };
        }));
      }

      // Update captions and sort_order for existing images if changed
      for (let i = 0; i < images.length; i++) {
        const img = images[i];
        if (img.id) {
          const original = existingImages.find((e) => e.id === img.id);
          if (
            original &&
            (original.caption !== img.caption || original.sort_order !== i)
          ) {
            await blogAPI.admin.updatePostImage(post.id, img.id, {
              caption: img.caption || null,
              sort_order: i,
            });
          }
        }
      }

      if (deletedImages.length > 0 || newImages.length > 0) {
        toast.success(`Đã cập nhật bài viết và ${newImages.length} ảnh mới!`);
      } else {
        toast.success('Cập nhật bài viết thành công!');
      }

      navigate(`/posts/${result.slug}`);
    } catch (error: any) {
      toast.error(error?.message || 'Không thể cập nhật bài viết');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (post) {
      navigate(`/posts/${post.slug}`);
    } else {
      navigate(-1);
    }
  };

  // Show error state
  if (loadError) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Không thể tải bài viết</p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Quay lại
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state - check if we have post matching slug and blog
  const isLoading = initialLoading || !post || post.slug !== slug || !blog;
  const isBlogMismatch = blog && post && blog.id !== post.blog_id;

  if (isLoading || isBlogMismatch) {
    return <Loading text="Đang tải bài viết..." description="Vui lòng đợi trong giây lát" />;
  }

  return (
    <div className="flex-1 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Quay lại
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold">Chỉnh Sửa Bài Viết</h1>
          <p className="text-gray-600 mt-1">Blog: {blog.title}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-base">
                Tiêu đề <span className="text-red-500">*</span>
              </Label>
              <AutoExpandTextarea
                id="title"
                placeholder="Nhập tiêu đề bài viết..."
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={loading}
                className="text-base md:text-lg font-medium"
                minRows={1}
                maxRows={3}
                autoFocus
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category" className="text-base">
                Danh mục
              </Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) =>
                  setFormData({ ...formData, categoryId: value })
                }
                disabled={loading}
              >
                <SelectTrigger className="text-base">
                  <SelectValue placeholder="Chọn danh mục (tùy chọn)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Không có danh mục</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content */}
            <div className="space-y-2">
              <Label htmlFor="content" className="text-base">
                Nội dung <span className="text-red-500">*</span>
              </Label>
              <AutoExpandTextarea
                id="content"
                placeholder="Bạn đang nghĩ gì?"
                value={formData.content}
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
                disabled={loading}
                minRows={3}
                maxRows={15}
                className="text-sm md:text-base"
              />
            </div>

            {/* Images */}
            <ImageUploadSection
              blogId={blog.id}
              images={images}
              onImagesChange={setImages}
              maxImages={50}
              maxVideos={3}
              disabled={loading}
              postId={post.id} // For organized folder: post-{id}/
              coverImageId={coverImageId}
              onCoverChange={setCoverImageId}
            />
          </div>

          {/* Action Buttons - Sticky on mobile */}
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 -mx-4 md:mx-0 md:relative md:bg-transparent md:border-0 md:p-0">
            <div className="flex gap-3 justify-end max-w-4xl mx-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 md:flex-initial"
              >
                Hủy
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.title.trim() || !formData.content.trim()}
                className="flex-1 md:flex-initial"
              >
                {loading ? (
                  <>
                    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] mr-2" />
                    Đang lưu...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Lưu Thay Đổi
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
